import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import TodoNoteYearInput from './TodoNoteYearInput';
import type { TodoNoteCategoryDraft, TodoNoteItemDraft, TodoRoutineCompleteMark } from '../../types/todoNote';
import {
  WD_SHORT,
  buildDueCalendarCells,
  isoFromYmd,
  parseYmd,
  tryParseMonthDayDot,
  tryParseMonthNum,
  tryParseMonthOnlyFromDayDraft,
  weekdayShortKo,
} from '../../utils/todoLikeDateInput';
import {
  DEFAULT_TODO_CATEGORY_NAME,
  TODO_NOTE_DELETE_HOLD_MS,
  joinTodoMemo,
  rowBackgroundStyle,
  sizeTextareaToContent,
  splitTodoMemo,
  todoEditorBlockTint,
  routineBadgeStyle,
} from './todoNoteRowShared';
import { BudgetNoteSearchMirroredInput } from '../budgetNote/BudgetNoteSearchHighlight';
import { SearchTextareaMirror } from '../shared/SearchTextareaMirror';
import { buildTodoItemFieldSearchMatchId } from '../../utils/todoNoteSearch';
import { matchesMaxWidth } from '../../utils/pageZoom';
import DeleteHoldButton from '../common/DeleteHoldButton';
import {
  hasTodoChecklist,
  toggleTodoChecklistAtCaret,
  toggleTodoChecklistLine,
  type TodoChecklistEdit,
} from '../../utils/todoChecklist';
import TodoChecklistText from './TodoChecklistText';

/** 타임테이블 행 삭제: 길게 누르면 바로 삭제, 짧게 누르면 확인 모달 */
const TodoNoteDeleteHoldButton: React.FC<{
  itemId: string;
  disabled: boolean;
  onRemove: () => void;
}> = ({ itemId, disabled, onRemove }) => (
  <DeleteHoldButton
    key={itemId}
    disabled={disabled}
    onDelete={onRemove}
    holdMs={TODO_NOTE_DELETE_HOLD_MS}
    className="cp-todo-note-remove-btn"
    holdBarClassName="cp-todo-note-remove-hold-bar"
    title="짧게 누르면 확인 후 삭제 · 길게 누르면 바로 삭제"
    ariaLabel="이 블럭 삭제 (짧게: 확인, 길게: 바로 삭제)"
    confirmTitle="이 TODO를 삭제할까요?"
    confirmDescription="삭제하면 목록에서 제거됩니다."
    data-tutorial-todo="row-delete"
  />
);

export interface SortableRowProps {
  item: TodoNoteItemDraft;
  ord: number;
  categories: TodoNoteCategoryDraft[];
  categoryInput: string;
  onCategoryInput: (v: string) => void;
  onPickCategory: (id: string | null) => void;
  onDueDate: (iso: string) => void;
  /** 일 모드에서 입력으로 월만 전환할 때 한 번에 반영 */
  onDueSwitchToMonthOnly: (iso: string) => void;
  /** 월만 모드에서 월.일 입력으로 일 모드 전환 */
  onDueSwitchToDay: (iso: string) => void;
  onMemoPatch: (title: string, body: string) => void;
  onMisc: (v: string) => void;
  onToggleComplete: () => void;
  onRemove: () => void;
  /** 타임테이블: 해당 행 바로 아래에 동일 내용 복제 */
  onDuplicate?: () => void;
  /** 루틴 발생행: 루틴 설정 수정 */
  onEditRoutine?: () => void;
  dragDisabled: boolean;
  selected: boolean;
  onSelect: () => void;
  /** 추가 모달용: 드래그·완료·삭제·선택 없이 블럭 필드만 */
  editorMode?: boolean;
  /** 추가 모달 열릴 때 메모 입력란에 포커스 */
  autoFocusMemo?: boolean;
  searchQuery?: string;
  activeMatchId?: string | null;
  /** 루틴 발생일 완료 마크 (없으면 completed 기준 V) */
  completeMark?: TodoRoutineCompleteMark | null;
  /** 루틴 가상행 배지 */
  routineBadge?: boolean;
}

export const SortableRow: React.FC<SortableRowProps> = ({
  item,
  ord,
  categories,
  categoryInput,
  onCategoryInput,
  onPickCategory,
  onDueDate,
  onDueSwitchToMonthOnly,
  onDueSwitchToDay,
  onMemoPatch,
  onMisc,
  onToggleComplete,
  onRemove,
  onDuplicate,
  onEditRoutine,
  dragDisabled,
  selected,
  onSelect,
  editorMode = false,
  autoFocusMemo = false,
  searchQuery = '',
  activeMatchId = null,
  completeMark = null,
  routineBadge = false,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled: dragDisabled || editorMode,
  });
  const dndStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.88 : undefined,
  };
  const cat = categories.find((c) => c.id === item.category_id);
  const rowStyle = rowBackgroundStyle(cat?.color);
  const catDotColor = cat?.color || '#ffffff';
  const catDotNeedsBorder =
    !cat ||
    cat.name.trim() === DEFAULT_TODO_CATEGORY_NAME ||
    catDotColor.toLowerCase() === '#ffffff';
  const catDotStyle: React.CSSProperties = {
    background: catDotColor,
    border: catDotNeedsBorder
      ? '1px solid color-mix(in srgb, var(--color-border) 78%, transparent)'
      : 'none',
  };
  const sortedRowCategories = useMemo(
    () => [...categories].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)),
    [categories]
  );

  const completed = item.completed;
  /** 루틴 발생행: 마감일은 발생일 자동값 — 직접 수정 불가 */
  const dueLocked = routineBadge;
  const mark: TodoRoutineCompleteMark | null =
    completeMark ?? (completed ? 'v' : null);
  const { y, mo, d } = parseYmd(item.due_date);
  const wd = item.due_kind === 'day' ? weekdayShortKo(y, mo, d) : '';

  const [mdEditOpen, setMdEditOpen] = useState(false);
  const [mdDraft, setMdDraft] = useState('');
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [duePickerOpen, setDuePickerOpen] = useState(false);
  const [dueCalView, setDueCalView] = useState<{ y: number; mo: number }>(() => ({ y, mo }));
  const [duePopoverPos, setDuePopoverPos] = useState<{ top: number; left: number } | null>(null);
  const [categoryMenuPos, setCategoryMenuPos] = useState<{ top: number; left: number; width: number } | null>(
    null
  );
  const mdInputRef = useRef<HTMLInputElement>(null);
  const categoryInputRef = useRef<HTMLInputElement>(null);
  const categoryMenuRef = useRef<HTMLDivElement>(null);
  const categoryMenuPortalRef = useRef<HTMLDivElement>(null);
  const catPickerBtnRef = useRef<HTMLButtonElement>(null);
  const duePickerWrapRef = useRef<HTMLDivElement>(null);
  const dueCalBtnRef = useRef<HTMLButtonElement>(null);
  const duePopoverRef = useRef<HTMLDivElement>(null);
  const memoTextareaRef = useRef<HTMLTextAreaElement>(null);
  const miscTextareaRef = useRef<HTMLTextAreaElement>(null);
  const mdBlurSuppressed = useRef(false);
  const memoCombined = joinTodoMemo(item.title, item.body);
  const memoHasChecklist = hasTodoChecklist(memoCombined);
  /** 목록 행: 검색 강조 미리보기와 겹치지 않도록 검색 중에는 그림 층 생략 */
  const rowMemoMirrored = memoHasChecklist && !searchQuery.trim();
  const applyMemoChecklistEdit = (edit: TodoChecklistEdit) => {
    const { title, body } = splitTodoMemo(edit.text);
    onMemoPatch(title, body);
    window.requestAnimationFrame(() => {
      const el = memoTextareaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(edit.caret, edit.caret);
      sizeTextareaToContent(el);
    });
  };
  const onMemoTextareaClick = (e: React.MouseEvent<HTMLTextAreaElement>) => {
    if (completed) return;
    const el = e.currentTarget;
    if (el.selectionStart !== el.selectionEnd) return;
    const edit = toggleTodoChecklistAtCaret(memoCombined, el.selectionStart);
    if (edit) applyMemoChecklistEdit(edit);
  };
  const memoChecklistMirror = memoHasChecklist ? (
    <div className="cp-todo-note-plain cp-todo-note-plain-memo cp-todo-memo-check-mirror" aria-hidden>
      <TodoChecklistText text={memoCombined} mirror />
      {'\n'}
    </div>
  ) : null;
  const renderMemoChecklistButton = (extraClass = '') =>
    (
      <button
        type="button"
        disabled={completed}
        className={'cp-todo-note-checklist-add-btn' + extraClass}
        title="커서가 있는 줄에 체크박스 넣기/빼기. 입력창의 체크박스를 누르면 체크·해제됩니다."
        aria-label="체크박스 넣기/빼기"
        onMouseDown={(e) => e.preventDefault()}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          const el = memoTextareaRef.current;
          const caret = el && document.activeElement === el ? el.selectionStart : memoCombined.length;
          applyMemoChecklistEdit(toggleTodoChecklistLine(memoCombined, caret));
        }}
      >
        <svg className="cp-todo-note-cat-picker-icon" viewBox="0 0 24 24" width="16" height="16" aria-hidden>
          <rect x="4" y="4" width="16" height="16" rx="3" fill="none" stroke="currentColor" strokeWidth="2" />
          <path
            d="M8 12.2l2.8 2.8L16.2 9.4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    );

  useLayoutEffect(() => {
    if (mdEditOpen && mdInputRef.current) {
      mdInputRef.current.focus();
      mdInputRef.current.select();
    }
  }, [mdEditOpen]);

  useEffect(() => {
    setMdEditOpen(false);
  }, [item.due_kind]);

  useEffect(() => {
    if (completed) setCategoryMenuOpen(false);
  }, [completed]);

  useEffect(() => {
    if (completed) setDuePickerOpen(false);
  }, [completed]);

  useEffect(() => {
    if (duePickerOpen) setDueCalView({ y, mo });
  }, [duePickerOpen, y, mo, item.due_date]);

  const dueCalCells = useMemo(() => buildDueCalendarCells(dueCalView.y, dueCalView.mo), [dueCalView.y, dueCalView.mo]);

  useEffect(() => {
    if (!categoryMenuOpen) return;
    const onDocPointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (categoryMenuRef.current?.contains(t)) return;
      if (categoryMenuPortalRef.current?.contains(t)) return;
      setCategoryMenuOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setCategoryMenuOpen(false);
    };
    document.addEventListener('pointerdown', onDocPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [categoryMenuOpen]);

  /** 모달 overflow에 잘리지 않도록 body portal + fixed 배치 */
  useLayoutEffect(() => {
    if (!categoryMenuOpen) {
      setCategoryMenuPos(null);
      return;
    }
    const place = () => {
      const anchor = categoryMenuRef.current ?? catPickerBtnRef.current;
      const menu = categoryMenuPortalRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const menuW = Math.max(rect.width, menu?.offsetWidth || 160);
      const menuH = menu?.offsetHeight || 180;
      const gap = 4;
      const margin = 8;
      let left = rect.left;
      left = Math.min(Math.max(margin, left), window.innerWidth - menuW - margin);
      let top = rect.bottom + gap;
      if (top + menuH > window.innerHeight - margin) {
        top = Math.max(margin, rect.top - menuH - gap);
      }
      setCategoryMenuPos({ top, left, width: Math.max(rect.width, 160) });
    };
    place();
    const raf = requestAnimationFrame(place);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [categoryMenuOpen, sortedRowCategories.length]);

  useEffect(() => {
    if (!duePickerOpen) return;
    const onDocPointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (duePickerWrapRef.current?.contains(t)) return;
      if (duePopoverRef.current?.contains(t)) return;
      setDuePickerOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDuePickerOpen(false);
    };
    document.addEventListener('pointerdown', onDocPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [duePickerOpen]);

  /** 모달 overflow에 잘리지 않도록 body portal + fixed 배치 */
  useLayoutEffect(() => {
    if (!duePickerOpen) {
      setDuePopoverPos(null);
      return;
    }
    const place = () => {
      const btn = dueCalBtnRef.current;
      const wrap = duePickerWrapRef.current;
      const btnVisible =
        btn != null &&
        btn.getClientRects().length > 0 &&
        window.getComputedStyle(btn).display !== 'none' &&
        window.getComputedStyle(btn).visibility !== 'hidden';
      const anchor = btnVisible ? btn : wrap;
      const pop = duePopoverRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const popW = pop?.offsetWidth || 260;
      const popH = pop?.offsetHeight || 300;
      const gap = 4;
      const margin = 8;
      let left = btnVisible ? rect.right - popW : rect.left;
      left = Math.min(Math.max(margin, left), window.innerWidth - popW - margin);
      let top = rect.bottom + gap;
      if (top + popH > window.innerHeight - margin) {
        top = Math.max(margin, rect.top - popH - gap);
      }
      setDuePopoverPos({ top, left });
    };
    place();
    const raf = requestAnimationFrame(place);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [duePickerOpen, dueCalView]);

  useLayoutEffect(() => {
    if (autoFocusMemo && memoTextareaRef.current) {
      memoTextareaRef.current.focus();
    }
  }, [autoFocusMemo]);

  useLayoutEffect(() => {
    sizeTextareaToContent(memoTextareaRef.current);
  }, [memoCombined, completed, editorMode]);

  useLayoutEffect(() => {
    sizeTextareaToContent(miscTextareaRef.current);
  }, [item.misc, completed, editorMode]);

  useEffect(() => {
    const onResize = () => {
      sizeTextareaToContent(memoTextareaRef.current);
      sizeTextareaToContent(miscTextareaRef.current);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const applyYear = (nextY: number) => {
    if (dueLocked || completed) return;
    if (!Number.isFinite(nextY)) return;
    const yy = Math.min(2100, Math.max(1900, Math.round(nextY)));
    if (item.due_kind === 'month') onDueDate(isoFromYmd(yy, mo, 1));
    else onDueDate(isoFromYmd(yy, mo, d));
  };

  const openMdEdit = () => {
    if (dueLocked || completed) return;
    setDuePickerOpen(false);
    mdBlurSuppressed.current = false;
    setMdDraft(item.due_kind === 'month' ? String(mo) : `${mo}.${d}`);
    setMdEditOpen(true);
  };

  const flushMdEditFromBlur = () => {
    if (mdBlurSuppressed.current) {
      mdBlurSuppressed.current = false;
      setMdEditOpen(false);
      return;
    }
    if (item.due_kind === 'month') {
      const full = tryParseMonthDayDot(mdDraft, y);
      if (full) {
        onDueSwitchToDay(isoFromYmd(y, full.mo, full.d));
      } else {
        const mm = tryParseMonthNum(mdDraft);
        if (mm !== null) onDueDate(isoFromYmd(y, mm, 1));
      }
    } else {
      const monthOnly = tryParseMonthOnlyFromDayDraft(mdDraft);
      if (monthOnly) {
        onDueSwitchToMonthOnly(isoFromYmd(y, monthOnly.mo, 1));
      } else {
        const parsed = tryParseMonthDayDot(mdDraft, y);
        if (parsed) onDueDate(isoFromYmd(y, parsed.mo, parsed.d));
      }
    }
    setMdEditOpen(false);
  };

  const cancelMdEdit = () => {
    mdBlurSuppressed.current = true;
    setMdEditOpen(false);
  };

  const openDuePicker = () => {
    if (dueLocked || completed) return;
    setCategoryMenuOpen(false);
    setMdEditOpen(false);
    setDuePickerOpen(true);
  };

  const isMobileTodoCard = () => matchesMaxWidth(900);

  const onDueMainHitClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (dueLocked || completed) return;
    if (isMobileTodoCard()) openDuePicker();
    else openMdEdit();
  };

  const onDueCellClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (dueLocked || completed) return;
    if ((e.target as HTMLElement).closest('.cp-todo-note-due-cal-btn')) return;
    if ((e.target as HTMLElement).closest('.cp-todo-note-due-picker-popover')) return;
    if (isMobileTodoCard()) {
      if (duePickerOpen) {
        setDuePickerOpen(false);
        return;
      }
      openDuePicker();
      return;
    }
    if ((e.target as HTMLElement).closest('.cp-todo-note-due-year-line')) return;
    if (duePickerOpen) {
      setDuePickerOpen(false);
      return;
    }
    if (mdEditOpen) {
      mdInputRef.current?.focus();
      return;
    }
    openMdEdit();
  };

  const onCategoryCellClick = () => {
    setDuePickerOpen(false);
    if (completed) return;
    if (isMobileTodoCard()) {
      setCategoryMenuOpen((v) => !v);
      return;
    }
    setCategoryMenuOpen(false);
    categoryInputRef.current?.focus();
  };

  const focusMemoField = () => {
    if (completed) return;
    memoTextareaRef.current?.focus();
  };

  const focusMiscField = () => {
    if (completed) return;
    miscTextareaRef.current?.focus();
  };

  const onMemoCellClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (completed) return;
    if ((e.target as HTMLElement).closest('textarea, .cp-budget-note-search-inline-preview')) return;
    focusMemoField();
  };

  const onMiscCellClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (completed) return;
    if ((e.target as HTMLElement).closest('textarea, .cp-budget-note-search-inline-preview')) return;
    focusMiscField();
  };

  const duePickerPopover =
    !dueLocked && duePickerOpen && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={duePopoverRef}
            className="cp-todo-note-due-picker-popover cp-todo-note-due-picker-popover--portal"
            role="dialog"
            aria-label="마감일 선택"
            style={
              duePopoverPos
                ? { top: duePopoverPos.top, left: duePopoverPos.left }
                : { top: -9999, left: -9999, visibility: 'hidden' }
            }
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="cp-todo-note-due-cal-head">
              <button
                type="button"
                className="cp-todo-note-due-cal-nav"
                aria-label="이전 달"
                disabled={dueCalView.y <= 1900 && dueCalView.mo <= 1}
                onClick={() =>
                  setDueCalView((v) => {
                    const next = v.mo <= 1 ? { y: v.y - 1, mo: 12 } : { y: v.y, mo: v.mo - 1 };
                    if (next.y < 1900) return { y: 1900, mo: 1 };
                    return next;
                  })
                }
              >
                ‹
              </button>
              <span className="cp-todo-note-due-cal-title">
                {dueCalView.y}년 {dueCalView.mo}월
              </span>
              <button
                type="button"
                className="cp-todo-note-due-cal-nav"
                aria-label="다음 달"
                disabled={dueCalView.y >= 2100 && dueCalView.mo >= 12}
                onClick={() =>
                  setDueCalView((v) => {
                    const next = v.mo >= 12 ? { y: v.y + 1, mo: 1 } : { y: v.y, mo: v.mo + 1 };
                    if (next.y > 2100) return { y: 2100, mo: 12 };
                    return next;
                  })
                }
              >
                ›
              </button>
            </div>
            <div className="cp-todo-note-due-cal-weekdays" aria-hidden>
              {WD_SHORT.map((w) => (
                <span key={w} className="cp-todo-note-due-cal-wd">
                  {w}
                </span>
              ))}
            </div>
            <div className="cp-todo-note-due-cal-grid">
              {dueCalCells.map((day, idx) =>
                day === null ? (
                  <span key={`e-${idx}`} className="cp-todo-note-due-cal-cell cp-todo-note-due-cal-cell--empty" />
                ) : (
                  <button
                    key={`d-${dueCalView.y}-${dueCalView.mo}-${day}`}
                    type="button"
                    className={`cp-todo-note-due-cal-day${
                      item.due_kind === 'day' &&
                      y === dueCalView.y &&
                      mo === dueCalView.mo &&
                      d === day
                        ? ' is-selected'
                        : ''
                    }`}
                    onClick={() => {
                      onDueSwitchToDay(isoFromYmd(dueCalView.y, dueCalView.mo, day));
                      setDuePickerOpen(false);
                    }}
                  >
                    {day}
                  </button>
                )
              )}
            </div>
            <button
              type="button"
              className={`cp-todo-note-due-cal-monthonly${
                item.due_kind === 'month' && y === dueCalView.y && mo === dueCalView.mo
                  ? ' is-active'
                  : ''
              }`}
              onClick={() => {
                onDueSwitchToMonthOnly(isoFromYmd(dueCalView.y, dueCalView.mo, 1));
                setDuePickerOpen(false);
              }}
            >
              이 달만 (월 단위 마감)
            </button>
          </div>,
          document.body
        )
      : null;

  const categoryMenuPopover =
    categoryMenuOpen && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={categoryMenuPortalRef}
            className="cp-todo-note-row-category-menu cp-todo-note-row-category-menu--portal"
            role="listbox"
            aria-label="카테고리 선택"
            style={
              categoryMenuPos
                ? {
                    top: categoryMenuPos.top,
                    left: categoryMenuPos.left,
                    width: categoryMenuPos.width,
                  }
                : { top: -9999, left: -9999, visibility: 'hidden' }
            }
            onMouseDown={(e) => e.stopPropagation()}
          >
            {sortedRowCategories.map((c) => {
              const isDefault = c.name.trim() === DEFAULT_TODO_CATEGORY_NAME;
              const isWhite = c.color.toLowerCase() === '#ffffff';
              const isActive = item.category_id === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={`cp-todo-note-row-category-option${isActive ? ' is-active' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPickCategory(c.id);
                    setCategoryMenuOpen(false);
                  }}
                >
                  <span
                    className="cp-todo-note-chip-dot"
                    style={{
                      background: c.color,
                      border:
                        isDefault || isWhite
                          ? '1px solid color-mix(in srgb, var(--color-border) 78%, transparent)'
                          : 'none',
                    }}
                    aria-hidden
                  />
                  <span>{c.name}</span>
                </button>
              );
            })}
          </div>,
          document.body
        )
      : null;

  if (editorMode) {
    const blockTint = todoEditorBlockTint(cat?.color);
    return (
      <div className="cp-todo-note-editor-blocks">
        <div className={`cp-todo-note-editor-block${blockTint.className}`} style={blockTint.style}>
          <div className="cp-todo-note-editor-block-head">마감일</div>
          <div className="cp-todo-note-editor-block-body">
            <div
              className={`cp-todo-note-cell cp-todo-note-cell--due${
                dueLocked ? ' cp-todo-note-cell--due-locked' : completed ? '' : ' cp-todo-note-cell--due-hit'
              }`}
              onClick={onDueCellClick}
              role="presentation"
              title={dueLocked ? '루틴 발생일의 마감일은 자동으로 설정됩니다' : undefined}
            >
              <div ref={duePickerWrapRef} className="cp-todo-note-due-cell-inner">
                <div className="cp-todo-note-due-stack">
                  <div className="cp-todo-note-due-year-line">
                    <TodoNoteYearInput
                      year={y}
                      disabled={dueLocked || completed}
                      onYearCommit={applyYear}
                      aria-label="연도"
                    />
                    <span className="cp-todo-note-due-year-suffix">년</span>
                  </div>
                  {mdEditOpen && !dueLocked ? (
                    <div className="cp-todo-note-due-main cp-todo-note-due-main--edit">
                      <input
                        ref={mdInputRef}
                        type="text"
                        inputMode="decimal"
                        className="cp-todo-note-due-dm-input"
                        disabled={completed}
                        value={mdDraft}
                        onChange={(e) => setMdDraft(e.target.value)}
                        onBlur={flushMdEditFromBlur}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            (e.target as HTMLInputElement).blur();
                          }
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            cancelMdEdit();
                          }
                        }}
                        placeholder="월.일"
                        aria-label={
                          item.due_kind === 'month'
                            ? '월만: 숫자 또는 월.일 (예: 4 / 4.5)'
                            : '월.일 입력 (예: 4.29, 월만: 4 / 4. / 4.0)'
                        }
                      />
                    </div>
                  ) : item.due_kind === 'month' ? (
                    <div className="cp-todo-note-due-main">
                      <button
                        type="button"
                        className="cp-todo-note-due-main-hit"
                        disabled={dueLocked || completed}
                        onClick={onDueMainHitClick}
                      >
                        <span className="cp-todo-note-due-main-text">{mo}월</span>
                      </button>
                    </div>
                  ) : (
                    <div className="cp-todo-note-due-main">
                      <button
                        type="button"
                        className="cp-todo-note-due-main-hit"
                        disabled={dueLocked || completed}
                        onClick={onDueMainHitClick}
                      >
                        <span className="cp-todo-note-due-main-text">
                          {mo}월 {d}일
                        </span>
                        <span className="cp-todo-note-due-weekday"> ({wd})</span>
                      </button>
                    </div>
                  )}
                </div>
                <button
                  ref={dueCalBtnRef}
                  type="button"
                  className="cp-todo-note-due-cal-btn"
                  disabled={dueLocked || completed}
                  aria-label="달력에서 마감일 선택"
                  aria-expanded={duePickerOpen}
                  aria-haspopup="dialog"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (dueLocked || completed) return;
                    setCategoryMenuOpen(false);
                    setDuePickerOpen((v) => !v);
                  }}
                >
                  <svg className="cp-todo-note-due-cal-icon" viewBox="0 0 24 24" width="16" height="16" aria-hidden>
                    <path d="M8 2v4M16 2v4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M4 10h16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M5 6h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                {duePickerPopover}
              </div>
            </div>
          </div>
        </div>
        <div className={`cp-todo-note-editor-block${blockTint.className}`} style={blockTint.style}>
          <div className="cp-todo-note-editor-block-head">카테고리</div>
          <div className="cp-todo-note-editor-block-body">
            <div className={`cp-todo-note-cell cp-todo-note-cell--cat${completed ? '' : ' cp-todo-note-cell--cat-hit'}`} onClick={onCategoryCellClick} role="presentation">
              <div ref={categoryMenuRef} className="cp-todo-note-cat-cell-inner">
                <div className="cp-todo-note-cat-wrap">
                  <span className="cp-todo-note-chip-dot cp-todo-note-cat-field-dot" style={catDotStyle} aria-hidden />
                  <input ref={categoryInputRef} type="text" className="cp-todo-note-plain cp-todo-note-plain-cat" readOnly={completed} value={categoryInput}
                    onChange={(e) => onCategoryInput(e.target.value)} placeholder="카테고리"
                    onBlur={() => { const exact = categories.find((c) => c.name.toLowerCase() === categoryInput.trim().toLowerCase()); if (exact) onPickCategory(exact.id); }} />
                </div>
                <button
                  type="button"
                  ref={catPickerBtnRef}
                  className="cp-todo-note-cat-picker-btn"
                  disabled={completed}
                  aria-label="카테고리 목록에서 선택"
                  aria-expanded={categoryMenuOpen}
                  aria-haspopup="listbox"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (completed) return;
                    setDuePickerOpen(false);
                    setCategoryMenuOpen((v) => !v);
                  }}
                >
                  <svg className="cp-todo-note-cat-picker-icon" viewBox="0 0 24 24" width="16" height="16" aria-hidden>
                    <path fill="currentColor" d="M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h16v2H4v-2z" />
                  </svg>
                </button>
                {categoryMenuPopover}
              </div>
            </div>
          </div>
        </div>
        <div className={`cp-todo-note-editor-block cp-todo-note-editor-block--wide${blockTint.className}`} style={blockTint.style}>
          <div className="cp-todo-note-editor-block-head cp-todo-note-editor-block-head--memo">
            <span>메모</span>
            {renderMemoChecklistButton()}
          </div>
          <div className="cp-todo-note-editor-block-body">
          <div className={'cp-todo-memo-check-wrap' + (memoHasChecklist ? ' is-mirrored' : '')}>
            {memoChecklistMirror}
            <textarea
              ref={memoTextareaRef}
              className="cp-todo-note-plain cp-todo-note-plain-memo"
              value={memoCombined}
              onInput={(e) => sizeTextareaToContent(e.currentTarget)}
              onChange={(e) => { const { title, body } = splitTodoMemo(e.target.value); onMemoPatch(title, body); }}
              onClick={onMemoTextareaClick}
              placeholder="제목 (첫 줄)과 내용을 입력하세요"
              readOnly={completed}
              rows={1}
              aria-label="메모 (첫 줄 제목, 이후 본문)"
              autoFocus={autoFocusMemo}
            />
          </div>
          </div>
        </div>
        <div className={`cp-todo-note-editor-block cp-todo-note-editor-block--wide${blockTint.className}`} style={blockTint.style}>
          <div className="cp-todo-note-editor-block-head">기타</div>
          <div className="cp-todo-note-editor-block-body">
            <textarea
              ref={miscTextareaRef}
              className="cp-todo-note-plain cp-todo-note-plain-misc"
              value={item.misc}
              onInput={(e) => sizeTextareaToContent(e.currentTarget)}
              onChange={(e) => onMisc(e.target.value)}
              placeholder="기타 메모"
              readOnly={completed}
              rows={1}
              aria-label="기타"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={completed ? dndStyle : { ...dndStyle, ...rowStyle }}
      className={`cp-todo-note-row${completed ? ' cp-todo-note-row--completed' : ''}${
        mark === 'x' ? ' cp-todo-note-row--routine-missed' : ''
      }${routineBadge ? ' cp-todo-note-row--routine' : ''}${selected ? ' is-selected' : ''}${
        categoryMenuOpen ? ' cp-todo-note-row--cat-menu-open' : ''
      }${duePickerOpen ? ' cp-todo-note-row--due-picker-open' : ''}`}
      data-todo-item-id={item.id}
      onClick={onSelect}
      onFocusCapture={onSelect}
    >
      <button
        type="button"
        className={`cp-todo-note-complete${completed ? ' is-done' : ''}${mark === 'x' ? ' is-auto-missed' : ''}`}
        aria-pressed={completed}
        aria-label={completed ? '완료 해제' : '완료'}
        data-tutorial-todo="row-complete"
        onClick={(e) => {
          e.stopPropagation();
          onToggleComplete();
        }}
        disabled={false}
      >
        {mark === 'v' ? (
          <svg
            className="cp-todo-note-complete-mark"
            viewBox="0 0 20 20"
            width="13"
            height="13"
            aria-hidden
            focusable="false"
          >
            <path
              d="M5.05 10.35 L9.25 14.65 L15.35 5.55"
              fill="none"
              stroke="currentColor"
              strokeWidth="3.15"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : mark === 'x' ? (
          <svg
            className="cp-todo-note-complete-mark cp-todo-note-complete-mark--x"
            viewBox="0 0 20 20"
            width="13"
            height="13"
            aria-hidden
            focusable="false"
          >
            <path
              d="M5.5 5.5 L14.5 14.5 M14.5 5.5 L5.5 14.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="3.15"
              strokeLinecap="round"
            />
          </svg>
        ) : null}
      </button>
      <div className="cp-todo-note-cells">
        <div
          className={`cp-todo-note-cell cp-todo-note-cell--ord${dragDisabled ? ' cp-todo-note-cell--ord-disabled' : ''}`}
          aria-label="순서 변경 드래그 영역"
          title={dragDisabled ? undefined : '드래그하여 같은 날짜(월) 블럭 순서 변경'}
          {...(dragDisabled ? {} : { ...attributes, ...listeners })}
        >
          {onEditRoutine ? (
            <button
              type="button"
              className="cp-todo-note-dup-btn"
              aria-label="루틴 설정 수정"
              title="루틴 설정 수정"
              onClick={(e) => {
                e.stopPropagation();
                onEditRoutine();
              }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <img
                src={`${process.env.PUBLIC_URL || ''}/edit.svg`}
                alt=""
                className="cp-todo-note-dup-icon"
                width={14}
                height={14}
                decoding="async"
              />
            </button>
          ) : null}
          {onDuplicate ? (
            <button
              type="button"
              className="cp-todo-note-dup-btn"
              aria-label="블럭 아래에 복제"
              title="아래에 복제"
              data-tutorial-todo="row-dup"
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate();
              }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <svg className="cp-todo-note-dup-icon" viewBox="0 0 24 24" width="14" height="14" aria-hidden>
                <path
                  fill="currentColor"
                  d="M16 1H4c-1.1 0-2 .9-2 2v12h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"
                />
              </svg>
            </button>
          ) : null}
          <div
            className={`cp-todo-note-drag-hit${dragDisabled ? ' cp-todo-note-drag-hit--disabled' : ''}`}
            aria-hidden
          />
          <span className="cp-todo-note-ord-num">{ord}</span>
        </div>
        <div className="cp-todo-note-due-cat-group">
        <div
          className={`cp-todo-note-cell cp-todo-note-cell--due${
            dueLocked ? ' cp-todo-note-cell--due-locked' : completed ? '' : ' cp-todo-note-cell--due-hit'
          }`}
          onClick={onDueCellClick}
          role="presentation"
          title={dueLocked ? '루틴 발생일의 마감일은 자동으로 설정됩니다' : undefined}
        >
          <div ref={duePickerWrapRef} className="cp-todo-note-due-cell-inner">
            <div className="cp-todo-note-due-stack">
              <div className="cp-todo-note-due-year-line">
                <TodoNoteYearInput
                  year={y}
                  disabled={completed}
                  onYearCommit={applyYear}
                  aria-label="연도"
                />
                <span className="cp-todo-note-due-year-suffix">년</span>
              </div>
              {mdEditOpen && !dueLocked ? (
                <div className="cp-todo-note-due-main cp-todo-note-due-main--edit">
                  <input
                    ref={mdInputRef}
                    type="text"
                    inputMode="decimal"
                    className="cp-todo-note-due-dm-input"
                    disabled={completed}
                    value={mdDraft}
                    onChange={(e) => setMdDraft(e.target.value)}
                    onBlur={flushMdEditFromBlur}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        (e.target as HTMLInputElement).blur();
                      }
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        cancelMdEdit();
                      }
                    }}
                    placeholder="월.일"
                    aria-label={
                      item.due_kind === 'month'
                        ? '월만: 숫자 또는 월.일 (예: 4 / 4.5)'
                        : '월.일 입력 (예: 4.29, 월만: 4 / 4. / 4.0)'
                    }
                  />
                </div>
              ) : item.due_kind === 'month' ? (
                <div className="cp-todo-note-due-main">
                  <button
                    type="button"
                    className="cp-todo-note-due-main-hit"
                    disabled={completed}
                    tabIndex={dueLocked ? -1 : undefined}
                    onClick={onDueMainHitClick}
                  >
                    <span className="cp-todo-note-due-main-text">{mo}월</span>
                  </button>
                </div>
              ) : (
                <div className="cp-todo-note-due-main">
                  <button
                    type="button"
                    className="cp-todo-note-due-main-hit"
                    disabled={completed}
                    tabIndex={dueLocked ? -1 : undefined}
                    onClick={onDueMainHitClick}
                  >
                    <span className="cp-todo-note-due-main-text">
                      {mo}월 {d}일
                    </span>
                    <span className="cp-todo-note-due-weekday"> ({wd})</span>
                  </button>
                </div>
              )}
            </div>
            <button
              ref={dueCalBtnRef}
              type="button"
              className="cp-todo-note-due-cal-btn"
              disabled={completed}
              tabIndex={dueLocked ? -1 : undefined}
              aria-label="달력에서 마감일 선택"
              aria-expanded={duePickerOpen}
              aria-haspopup="dialog"
              aria-disabled={dueLocked || undefined}
              onClick={(e) => {
                e.stopPropagation();
                if (dueLocked || completed) return;
                setCategoryMenuOpen(false);
                setDuePickerOpen((v) => !v);
              }}
            >
              <svg className="cp-todo-note-due-cal-icon" viewBox="0 0 24 24" width="16" height="16" aria-hidden>
                <path
                  d="M8 2v4M16 2v4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M4 10h16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M5 6h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {duePickerPopover}
          </div>
        </div>
        <div
          className={`cp-todo-note-cell cp-todo-note-cell--cat${completed ? '' : ' cp-todo-note-cell--cat-hit'}`}
          onClick={onCategoryCellClick}
          role="presentation"
        >
          <div ref={categoryMenuRef} className="cp-todo-note-cat-cell-inner">
            <div className="cp-todo-note-cat-wrap">
              <span className="cp-todo-note-chip-dot cp-todo-note-cat-field-dot" style={catDotStyle} aria-hidden />
              <BudgetNoteSearchMirroredInput
                className="cp-todo-note-plain cp-todo-note-plain-cat"
                value={categoryInput}
                query={searchQuery}
                activeMatchId={activeMatchId}
                buildMatchId={(start) => buildTodoItemFieldSearchMatchId(item.id, 'category', start)}
                onChange={onCategoryInput}
                ariaLabel="카테고리"
                readOnly={completed}
                inputRef={categoryInputRef}
                onBlur={() => {
                  const exact = categories.find((c) => c.name.toLowerCase() === categoryInput.trim().toLowerCase());
                  if (exact) onPickCategory(exact.id);
                }}
              />
            </div>
            <button
              type="button"
              ref={catPickerBtnRef}
              className="cp-todo-note-cat-picker-btn"
              disabled={completed}
              aria-label="카테고리 목록에서 선택"
              aria-expanded={categoryMenuOpen}
              aria-haspopup="listbox"
              onClick={(e) => {
                e.stopPropagation();
                if (completed) return;
                setDuePickerOpen(false);
                setCategoryMenuOpen((v) => !v);
              }}
            >
              <svg className="cp-todo-note-cat-picker-icon" viewBox="0 0 24 24" width="16" height="16" aria-hidden>
                <path
                  fill="currentColor"
                  d="M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h16v2H4v-2z"
                />
              </svg>
            </button>
            {categoryMenuPopover}
          </div>
        </div>
        </div>
        <div
          className={`cp-todo-note-cell cp-todo-note-cell--memo${routineBadge ? ' has-routine-badge' : ''}`}
          onClick={onMemoCellClick}
          role="presentation"
        >
          {routineBadge ? (
            <span className="cp-todo-note-routine-badge" style={routineBadgeStyle(cat?.color)} aria-hidden>
              루틴
            </span>
          ) : null}
          <div
            className={
              'cp-todo-memo-check-wrap cp-todo-memo-check-wrap--row' +
              (rowMemoMirrored ? ' is-mirrored' : '') +
              ' has-checklist-btn'
            }
          >
            {rowMemoMirrored ? memoChecklistMirror : null}
            <SearchTextareaMirror
              value={memoCombined}
              query={searchQuery}
              activeMatchId={activeMatchId}
              buildMatchId={(start) => buildTodoItemFieldSearchMatchId(item.id, 'memo', start)}
              className="cp-todo-note-plain cp-todo-note-plain-memo"
              ariaLabel="메모 (첫 줄 제목, 이후 본문)"
              onInput={(e) => sizeTextareaToContent(e.currentTarget)}
              onClick={onMemoTextareaClick}
              onChange={(v) => {
                const { title, body } = splitTodoMemo(v);
                onMemoPatch(title, body);
              }}
              readOnly={completed}
              rows={1}
              textareaRef={memoTextareaRef}
            />
            {renderMemoChecklistButton(' cp-todo-note-checklist-add-btn--row')}
          </div>
        </div>
        <div
          className="cp-todo-note-cell cp-todo-note-cell--misc"
          onClick={onMiscCellClick}
          role="presentation"
        >
          <SearchTextareaMirror
            value={item.misc}
            query={searchQuery}
            activeMatchId={activeMatchId}
            buildMatchId={(start) => buildTodoItemFieldSearchMatchId(item.id, 'misc', start)}
            className="cp-todo-note-plain cp-todo-note-plain-misc"
            ariaLabel="기타"
            onInput={(e) => sizeTextareaToContent(e.currentTarget)}
            onChange={onMisc}
            readOnly={completed}
            rows={1}
            textareaRef={miscTextareaRef}
          />
        </div>
        <div className="cp-todo-note-cell cp-todo-note-cell--actions">
          {onEditRoutine ? (
            <button
              type="button"
              className="cp-todo-note-dup-btn cp-todo-note-dup-btn--mobile"
              aria-label="루틴 설정 수정"
              title="루틴 설정 수정"
              onClick={(e) => {
                e.stopPropagation();
                onEditRoutine();
              }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <img
                src={`${process.env.PUBLIC_URL || ''}/edit.svg`}
                alt=""
                className="cp-todo-note-dup-icon"
                width={14}
                height={14}
                decoding="async"
              />
            </button>
          ) : null}
          {onDuplicate ? (
            <button
              type="button"
              className="cp-todo-note-dup-btn cp-todo-note-dup-btn--mobile"
              aria-label="블럭 아래에 복제"
              title="아래에 복제"
              data-tutorial-todo="row-dup"
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate();
              }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <svg className="cp-todo-note-dup-icon" viewBox="0 0 24 24" width="14" height="14" aria-hidden>
                <path
                  fill="currentColor"
                  d="M16 1H4c-1.1 0-2 .9-2 2v12h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"
                />
              </svg>
            </button>
          ) : null}
          <TodoNoteDeleteHoldButton itemId={item.id} disabled={completed} onRemove={onRemove} />
        </div>
      </div>
    </div>
  );
};
